import { convertDM, convertDMS, Vec2 } from "@nova/calculate-geospatial"
import { selectBorderData, selectCoordinateSystem } from "../PDFSlice"
import store from "integrations/redux/store"
import { WebMercatorViewport } from "@deck.gl/core"
import proj4 from "proj4"
import { CRS, Notation } from "@nova/component-crs-selector"

export type GridData = { x: number; y: number; offsetX: number; offsetY: number }
export type GridVertex = { point: Vec2; edge: number }
export type GridLine = [GridVertex, GridVertex]

const floor = (num: number, mult: number) => {
    return Math.floor(num / mult) * mult
}

const ceil = (num: number, mult: number) => {
    return Math.ceil(num / mult) * mult
}

export type CoordinateSystem = {
    proj4: string
    notations: Record<string, Notation>
    code: string
    name: string
}

export const projectionLabel = (proj: string) => (proj === "longlat" ? "lat long" : proj)

export const projectCRS = ([lng, lat]: number[], crs?: CRS) => {
    try {
        const coordinateSystem = crs?.name || selectCoordinateSystem()(store.getState())
        return proj4("WGS84", coordinateSystems[coordinateSystem].proj4, [lng, lat])
    } catch (err) {
        console.log(err, lng, lat)
        return [0, 0]
    }
}

export const unprojectCRS = ([x, y]: number[], crs?: CRS) => {
    try {
        const coordinateSystem = crs?.name || selectCoordinateSystem()(store.getState())
        return proj4(coordinateSystems[coordinateSystem].proj4, "WGS84", [x, y])
    } catch (err) {
        console.error(err, x, y)
        return [0, 0]
    }
}

export const getDecimals = (num: number) => num.toString().split(".")[1]?.length || 0

export const getLinePoints = (
    [topLeft, topRight, bottomRight, bottomLeft]: [Vec2, Vec2, Vec2, Vec2],
    view: WebMercatorViewport,
    data: GridData,
    crs?: CRS
) => {
    const lines: GridLine[] = []
    const topLeftProj = projectCRS(view.unproject(topLeft), crs)
    const topRightProj = projectCRS(view.unproject(topRight), crs)
    const bottomRightProj = projectCRS(view.unproject(bottomRight), crs)
    const bottomLeftProj = projectCRS(view.unproject(bottomLeft), crs)
    const worldQuad = [topLeftProj, topRightProj, bottomRightProj, bottomLeftProj]
    const clientQuad = [topLeft, topRight, bottomRight, bottomLeft]
    const allLngs = worldQuad.map((p) => p[0])
    const allLats = worldQuad.map((p) => p[1])
    const allXs = clientQuad.map((p) => p[0])
    const allYs = clientQuad.map((p) => p[1])

    const minLng = Math.min(...allLngs)
    const maxLng = Math.max(...allLngs)
    const minLat = Math.min(...allLats)
    const maxLat = Math.max(...allLats)

    const minX = Math.min(...allXs)
    const maxX = Math.max(...allXs)
    const minY = Math.min(...allYs)
    const maxY = Math.max(...allYs)

    let steps = 0
    for (
        let lng = floor(minLng, data.x) + (data.offsetX % data.x);
        lng < ceil(maxLng, data.x) + (data.offsetX % data.x);
        lng += data.x
    ) {
        if (steps > 50) break
        const intersections: GridVertex[] = []
        for (let i = 0; i < 4; i++) {
            const p1 = worldQuad[i]
            const p2 = worldQuad[(i + 1) % 4]
            if ((p1[0] - lng) * (p2[0] - lng) <= 0 && p1[0] !== p2[0]) {
                const t = (lng - p1[0]) / (p2[0] - p1[0])
                const lat = p1[1] + t * (p2[1] - p1[1])
                intersections.push({ point: [lng, lat], edge: i })
            }
        }
        if (intersections.length === 2) {
            const [a, b] = intersections.map(({ point, edge }) => {
                const newPoint = view.project(unprojectCRS([point[0], point[1]], crs)) as Vec2
                newPoint[0] -= minX
                newPoint[1] -= minY
                return { point: newPoint, edge }
            })
            if ((a.point[0] > 0 && a.point[0] < maxX) || (b.point[0] > 0 && b.point[0] < maxX)) {
                const line = [a, b] as GridLine
                lines.push(line)
            } else steps++
        } else steps++
        if (lines.length > 100) break
    }
    steps = 0
    for (
        let lat = floor(minLat, data.y) + (data.offsetY % data.y);
        lat < ceil(maxLat, data.y) + (data.offsetY % data.y);
        lat += data.y
    ) {
        if (steps > 50) break
        const intersections: GridVertex[] = []
        for (let i = 0; i < 4; i++) {
            const p1 = worldQuad[i]
            const p2 = worldQuad[(i + 1) % 4]
            if ((p1[1] - lat) * (p2[1] - lat) <= 0 && p1[1] !== p2[1]) {
                const t = (lat - p1[1]) / (p2[1] - p1[1])
                const lng = p1[0] + t * (p2[0] - p1[0])
                intersections.push({ point: [lng, lat], edge: i })
            }
        }
        if (intersections.length === 2) {
            const [a, b] = intersections.map(({ point, edge }) => {
                const newPoint = view.project(unprojectCRS([point[0], point[1]], crs)) as Vec2
                newPoint[0] -= minX
                newPoint[1] -= minY
                return { point: newPoint, edge }
            })
            if ((a.point[1] > 0 && a.point[1] < maxY) || (b.point[1] > 0 && b.point[1] < maxY)) {
                const line = [a, b] as GridLine
                lines.push(line)
            } else steps++
        } else steps++
        if (lines.length > 100) break
    }
    return lines
}

const dms: Notation = {
    id: "dms",
    label: "Degrees Minutes Seconds",
    getTick: ([lng, lat]) => {
        const dms = convertDMS(lng, lat)
        return [dms[1], dms[0]]
    },
}

const degreesMinutes: Notation = {
    id: "degreesMinutes",
    label: "Degrees Decimal Minutes",
    getTick: ([lng, lat]) => {
        const borderData = selectBorderData()(store.getState())
        const xDecimals = Math.max(getDecimals(borderData.x), getDecimals(borderData.offsetX))
        const yDecimals = Math.max(getDecimals(borderData.y), getDecimals(borderData.offsetY))
        const dm = convertDM(lng, lat, Math.max(xDecimals - 1, 0), Math.max(yDecimals - 1, 0))
        return [dm[1], dm[0]]
    },
}

const decimal: Notation = {
    id: "decimal",
    label: "Decimal Degrees",
    getTick: ([lng, lat]) => {
        const borderData = selectBorderData()(store.getState())
        const xDecimals = Math.max(getDecimals(borderData.x), getDecimals(borderData.offsetX))
        const yDecimals = Math.max(getDecimals(borderData.y), getDecimals(borderData.offsetY))
        return [lng.toFixed(xDecimals), lat.toFixed(yDecimals)]
    },
}

// const meters: Notation = {
//     id: "meters",
//     label: "Meters",
//     getTick: ([x, y]) => {
//         return [Math.round(x).toString(), Math.round(y).toString()]
//     },
// }

export const coordinateSystems: Record<string, CoordinateSystem> = {
    "WGS 84": {
        code: "4326",
        name: "WGS 84",
        proj4: "+proj=longlat +datum=WGS84 +no_defs +type=crs",
        notations: {
            decimal,
            degreesMinutes,
            dms,
        },
    },
}
if (typeof window !== "undefined" && !process.env.VITEST) {
    fetch("/crs.json")
        .then((res) => res.json())
        .then((data) => {
            data.forEach(
                ({
                    code,
                    name,
                    proj4,
                }: {
                    code: string
                    name: string
                    proj4: string
                    unit: string
                }) => {
                    coordinateSystems[name] = {
                        code,
                        name,
                        proj4,
                        notations: {
                            decimal,
                            degreesMinutes,
                            dms,
                        },
                    }
                }
            )
        })
        .catch((err) => {
            console.error(err)
        })
}
